import React from 'react'
import PropTypes from 'prop-types'
import styles from './InputContainer.module.scss'
import cls from 'classnames'
import Badge from './Badge'

/** Container for embracing input or textarea based fields such as Text, ZipCode, Date, etc. */
const InputContainer = ({
  children,
  label = null,
  hasError = false,
  filled = false,
  disabled = false,
  focus = false,
  sensitive = false,
  leftBadge = null,
  rightBadge = null,
  hideCheckIcon = false,
  appearance = 'default',
}) => {
    const inputStyles = cls(
      styles.input,
      {
        [styles.inputWithFloatingLabel]: appearance === 'default',
        [styles.roundEdges]: !(!!leftBadge || !!rightBadge),
        [styles.roundEdgesRight]: !!leftBadge,
        [styles.roundEdgesLeft]: !!rightBadge,
        [styles.showLabel]: appearance === 'default' && filled,
        [styles.filled]: !disabled && !hasError && filled,
        [styles.disabled]: disabled,
        [styles.disabledBg]: disabled,
        [styles.compact]: appearance === 'compact',
        [styles.compactWithLeftBadge]: appearance === 'compact' && !!leftBadge,
        [styles.compactWithRightBadge]: appearance === 'compact' && !!rightBadge,
        inspectletIgnore: sensitive
      },
    ),
    containerStyles = cls (
      styles.container,
      {
        [styles.containerShowLabel]: filled,
        [styles.containerDisabled]: disabled,
        [styles.filled]: !disabled && !hasError && filled,
        [styles.hasError]: hasError,
        [styles.disabledBg]: disabled,
      }
    ),
    hideAllIcons = appearance === 'compact',
    hasIcon = !hideCheckIcon && ((!disabled && hasError) || (!hasError && !disabled && !focus && filled) || disabled) && !hideAllIcons

  return (
    <div>
      {appearance === 'compact' && <div className={cls(styles.label, 'disclaimer semiBold', { [styles.disabled]: disabled })}>{label}</div>}
      <div className={cls(containerStyles, styles.roundEdges)}>
        {leftBadge && <Badge content={leftBadge} position="left" appearance={appearance === 'compact' ? 'transparent' : 'pill'} />}
        {appearance === 'default' &&  <span className={cls('disclaimer semiBold', styles.floatingLabel, {[styles.labelWithLeftBadge]: !!leftBadge})}>{label}</span>}
        {children({inputStyles, hasIcon})}
        {rightBadge && <Badge content={rightBadge} position="right" appearance={appearance === 'compact' ? 'transparent' : 'pill'} className={cls({ [styles.disabledFont]: disabled })} />}
        {!hideAllIcons && !disabled && hasError && <i className={cls("cred-icon", "icon-warning", styles.icon, styles.iconWarning, {[styles.iconWithBadge]: !!rightBadge})} data-testid="iconWarning"/>}
        {!hideAllIcons && !hideCheckIcon && !hasError && !disabled && !focus && filled && <i className={cls("cred-icon", "icon-check", styles.icon, styles.iconCheck, {[styles.iconWithBadge]: !!rightBadge})} data-testid="iconCheck"/>}
        {!hideAllIcons && disabled && <i className={cls("cred-icon", "icon-lock-hole", styles.icon, styles.iconCheck, {[styles.iconWithBadge]: !!rightBadge})} data-testid="iconDisabled"/>}
      </div>
    </div>
  )
}

InputContainer.propTypes = {
  /** The label displayed to the user after the field has any value */
  label: PropTypes.string,
  /** Indicates there's a validation error in the input */
  hasError: PropTypes.bool,
  /** Indicates the input was filled correctly */
  filled: PropTypes.bool,
  /** Displays a lock icon when set to true */
  disabled: PropTypes.bool,
  /** Children should be a render-props function */
  children: PropTypes.func,
  /** True if the field has the cursor in it */
  focus: PropTypes.bool,
  /** Provides an additional css class to the input to instruct analytics libs (specially inspectlet) not to store the field value */
  sensitive: PropTypes.bool,
  /** Adds a badge on the left part of the field with either a cred-icon if a string is provided, or any other content if a react node is provided */
  leftBadge: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Adds a badge on the right part of the field with either a cred-icon if a string is provided, or any other content if a react node is provided */
  rightBadge: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** True if hide the check icon for filled field */
  hideCheckIcon: PropTypes.bool,
  /** default: regular input with grey right badge, icons, inner label and 59px height. compact: white right badge, no icons, separate label and 40px height */
  appearance: PropTypes.oneOf(['default', 'compact'])
}

export default InputContainer
